import { LocaleService } from '@maximizer/core/shared/data-access';
import {
  AppointmentTimelineEvent,
  DocumentTimelineEvent,
  InteractionTimelineEvent,
  NoteTimelineEvent,
  Octopus,
  TaskTimelineEvent,
  TimelineEvent,
  Translation,
} from '@maximizer/core/shared/domain';
import { ResourceOrTranslatePipe } from '@maximizer/core/shared/ui/pipes';
import { dateComparator } from '@maximizer/core/shared/ui/sorting';
import { capitalize } from '@maximizer/shared/util';

export class TimelineEventTextBuilder extends Array<string> {
  constructor(
    private readonly resourceOrTranslate: ResourceOrTranslatePipe,
    private readonly locale: LocaleService,
    private readonly event: TimelineEvent,
  ) {
    super();
  }

  appointment(): this {
    const appointment = this.event as AppointmentTimelineEvent;
    if (appointment.hasTime) {
      this.push(
        `${this.locale.formatDate(this.event.date.value, 'userDateTime')} - ${this.locale.formatTimeUserLocale(appointment.endDate?.value)}`,
      );
    } else {
      this.date();
    }

    return this.with(
      {
        resourceID: 'JSS_GenTxtAppt',
        translationID: 'timeline.appointment.type',
      },
      {
        resourceID: 'JSS_GenApptWith',
        translationID: 'timeline.appointment.with',
      },
    );
  }

  task(): this {
    return this.date().with(
      { resourceID: 'JSS_GenTxtTask', translationID: 'timeline.task.type' },
      { resourceID: 'JSS_GenTextTaskWith', translationID: 'timeline.task.for' },
    );
  }

  email(): this {
    return this.date().add(
      `${this.resourceOrTranslate.transform('JSS_GenTxtEmailFor', 'timeline.email.for')} '${this.event.with?.display}'`,
    );
  }

  note(): this {
    return this.date()
      .add(
        `${this.resourceOrTranslate.transform('JSS_GenTxtNoteFor', 'timeline.note.for')} '${this.event.with?.display}'`,
      )
      .add(this.event.description ?? '');
  }

  document(): this {
    const event = this.event as DocumentTimelineEvent;
    const documentFor = this.resourceOrTranslate.transform(
      'JSS_GenTxtDocumentFor',
      'timeline.document.for',
    );
    const addedBy = this.resourceOrTranslate.transform(
      'JSS_GenTextAddedByLc',
      'timeline.document.addedBy',
    );
    return this.date().add(
      `${documentFor} '${this.event.with?.display}' ${addedBy} '${this.event.creator.display}': '${event.file}'`,
    );
  }

  interaction(): this {
    const event = this.event as InteractionTimelineEvent;
    if (Octopus.DefaultInteractions.isCall(event.interaction.type)) {
      const resourceID = 'JSS_GenTxtPhoneCall' + capitalize(event.direction);
      const translationID = 'timeline.call.' + event.direction;

      const callSource = this.resourceOrTranslate.transform(
        resourceID,
        translationID,
      );
      const callTarget = this.resourceOrTranslate.transform(
        resourceID + 'Response',
        translationID + 'Response',
      );

      return this.date().add(
        `${callSource} '${event.with?.display}' ${callTarget} '${event.creator.display}'`,
      );
    }

    const resourceID = 'JSS_GenTxtDynamicMessage' + capitalize(event.direction);
    const translationID = 'timeline.interaction.' + event.direction;
    const source = this.locale.format(
      this.resourceOrTranslate.transform(resourceID, translationID),
      { type: event.interaction.display },
    );
    const target = this.resourceOrTranslate.transform(
      resourceID + 'Response',
      translationID + 'Response',
    );

    return this.date().add(
      `${source} '${event.with?.display}' ${target} '${event.creator.display}'`,
    );
  }

  date(): this {
    this.push(
      `${this.resourceOrTranslate.transform('JSS_GenTxtDateTimeLabel', 'timeline.columns.datetime')}: ${this.locale.formatDate(this.event.date.value, 'userDateTime')}`,
    );
    return this;
  }

  with(typeTranslation: Translation, typeWithTranslation: Translation): this {
    if (this.event.with) {
      this.push(
        `${this.resourceOrTranslate.transform(typeWithTranslation.resourceID, typeWithTranslation.translationID)} '${this.event.with?.display}'`,
      );
    } else {
      this.push(
        this.resourceOrTranslate.transform(
          typeTranslation.resourceID,
          typeTranslation.translationID,
        ),
      );
    }
    return this;
  }

  subject(): this {
    if (this.event.subject) {
      this.push(
        `${this.resourceOrTranslate.transform('JSS_GenTxtSubject', 'timeline.columns.subject')}: ${this.event.subject}`,
      );
    }
    return this;
  }

  description(): this {
    if (this.event.description) {
      this.push(
        `${this.resourceOrTranslate.transform('JSS_GenTxtDescription', 'timeline.description')}: ${this.event.description}`,
      );
    }
    return this;
  }

  regarding(): this {
    if (this.event.regarding) {
      this.push(
        `${this.resourceOrTranslate.transform('JSS_GenTxtRegarding', 'timeline.columns.regarding')}: '${this.event.regarding.display}'`,
      );
    }
    return this;
  }

  category(): this {
    if (this.event.category) {
      this.push(
        `${this.resourceOrTranslate.transform('JSS_GenTxtCategory', 'timeline.columns.category')}: ${this.event.category}`,
      );
    }
    return this;
  }

  result(): this {
    if (this.event.result) {
      this.push(
        `${this.resourceOrTranslate.transform('JSS_GenTxtResult', 'timeline.result')}: ${this.event.result}`,
      );
    }
    return this;
  }

  priority(): this {
    if (this.event.priority) {
      this.push(
        `${this.resourceOrTranslate.transform('JSS_GenTextPriority', 'activityManager.columns.priority')}: '${this.event.priority}'`,
      );
    }
    return this;
  }

  assignedTo(): this {
    const task = this.event as TaskTimelineEvent;
    if (task.assignedTo?.length) {
      this.push(
        `${this.resourceOrTranslate.transform('JSS_GenTextAssignedTo', 'timeline.assignedTo')}: '${task.assignedTo.map((item) => item.display).join(', ')}'`,
      );
    }
    return this;
  }

  completed(): this {
    const task = this.event as TaskTimelineEvent;
    if (task.isDone) {
      this.push(
        `(${this.resourceOrTranslate.transform('JSS_GenTextCompleted', 'activityManager.columns.completed')})`,
      );
    }
    return this;
  }

  add(value: string): this {
    this.push(value);
    return this;
  }

  build(separator = '\n'): string {
    return this.join(separator);
  }
}

export const filterEvent = (event: TimelineEvent): boolean => {
  if (event.type === 'appointment') {
    return !(event as AppointmentTimelineEvent).private;
  } else if (event.type === 'note') {
    return (
      (event as NoteTimelineEvent).noteType === Octopus.DefaultNotes.MANUAL &&
      (event.description ?? '').length > 0
    );
  }

  return (event.description ?? event.subject ?? '').length > 0;
};

export const formatEvent = (
  resourceOrTranslate: ResourceOrTranslatePipe,
  locale: LocaleService,
  event: TimelineEvent,
  separator = '\n',
): string => {
  const builder = new TimelineEventTextBuilder(
    resourceOrTranslate,
    locale,
    event,
  );

  switch (event.type) {
    case 'email':
      event.description = event.description?.replace(
        /From:.*(Sent|Date):.*To:.*/gs,
        '',
      );
      return builder
        .email()
        .subject()
        .add(event.description ?? '')
        .category()
        .result()
        .build(separator);

    case 'appointment':
      return builder
        .appointment()
        .subject()
        .description()
        .regarding()
        .category()
        .result()
        .build(separator);

    case 'task':
      return builder
        .task()
        .subject()
        .priority()
        .assignedTo()
        .regarding()
        .category()
        .result()
        .completed()
        .build(separator);

    case 'interaction':
      return builder
        .interaction()
        .subject()
        .description()
        .regarding()
        .category()
        .result()
        .build(separator);

    case 'document':
      return builder
        .document()
        .category()
        .description()
        .build(separator);

    case 'note':
      return builder.note().build(separator);

    default:
      return builder
        .date()
        .add(event.description ?? '')
        .build(separator);
  }
};

export const getTimelineTexts = (
  resourceOrTranslate: ResourceOrTranslatePipe,
  locale: LocaleService,
  events: TimelineEvent[],
  separator = '\n',
): string[] =>
  events
    .filter(filterEvent)
    .sort((first, second) =>
      dateComparator(first.date.value, second.date.value),
    )
    .map((event) => formatEvent(resourceOrTranslate, locale, event, separator));
